import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

export default function AdminLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user_data = localStorage.getItem("userInfo");
  const user = JSON.parse(user_data);

  useEffect(() => {
    if (!user?.user?.is_staff) {
      navigate("/auth/login");
    }
  }, [user]);

  const navigationItems = [
    {
      label: "Dashboard",
      path: "/admin",
      icon: "ri-dashboard-fill",
      color: "text-blue-600",
    },
    {
      label: "Users Management",
      path: "/admin/users",
      icon: "ri-user-settings-fill",
      color: "text-purple-600",
    },
    {
      label: "Transactions",
      path: "/admin/transactions",
      icon: "ri-file-list-3-fill",
      color: "text-purple-600",
    },
    {
      label: "Network Management",
      path: "/admin/networks",
      icon: "ri-wifi-fill",
      color: "text-orange-600",
    },
    {
      label: "Data Plans",
      path: "/admin/data-plans",
      icon: "ri-database-2-fill",
      color: "text-indigo-600",
    },
    {
      label: "Electricity",
      path: "/admin/electricity",
      icon: "ri-flashlight-fill",
      color: "text-yellow-600",
    },
    {
      label: "Cable TV",
      path: "/admin/cable-tv",
      icon: "ri-tv-fill",
      color: "text-red-600",
    },
    {
      label: "Exam Pins",
      path: "/admin/exam-pins",
      icon: "ri-file-text-fill",
      color: "text-pink-600",
    },
    {
      label: "API Providers",
      path: "/admin/api-providers",
      icon: "ri-code-s-slash-fill",
      color: "text-cyan-600",
    },
    {
      label: "Loan Management",
      path: "/admin/loans",
      icon: "ri-hand-coin-fill",
      color: "text-emerald-600",
    },
    {
      label: "Loan Settings",
      path: "/admin/loan-settings",
      icon: "ri-settings-4-fill",
      color: "text-pink-600",
    },
    {
      label: "Platform Settings",
      path: "/admin/settings",
      icon: "ri-settings-3-fill",
      color: "text-gray-600",
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
              <i className="ri-admin-fill text-white text-lg"></i>
            </div>
            <span className="font-bold text-xl text-gray-900">Admin Panel</span>
          </div>
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-menu-fill text-xl"></i>
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                <i className="ri-admin-fill text-white text-lg"></i>
              </div>
              <span className="font-bold text-xl text-gray-900">
                Admin Panel
              </span>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto py-4">
            <nav className="space-y-1 px-3">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    isActive(item.path)
                      ? "bg-red-50 text-red-700 border-r-2 border-red-600"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <div
                    className={`w-5 h-5 flex items-center justify-center ${
                      isActive(item.path) ? "text-red-600" : item.color
                    }`}
                  >
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Admin Info */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <i className="ri-shield-user-fill text-red-600 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  Super Admin
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.user?.email}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          ></div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
